import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { defaultImagePlaceholder } from '../../../constants/images.constants';
import { DynamicGalleryService } from '../../../core/services/dynamic-gallery/dynamic-gallery.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { NativeAppService } from '@shared/common/services/native-app/native-app.service';
import type { SwiperOptions } from 'swiper/types';
import Swiper from 'swiper';

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  standalone: false
})
export class GalleryComponent implements OnInit, AfterViewInit {
  galleryPics;
  title: string;
  imageIndex: number;
  isShowTags: boolean;
  isRoomCategory: boolean;
  swiperConfig: SwiperOptions = {
    spaceBetween: 10,
    slidesPerView: 1,
    observer: true,
    pagination: {
      dynamicBullets: true
    },
    keyboard: {
      enabled: true,
      onlyInViewport: false
    }
  };
  swiperThumbsConfig: SwiperOptions = {
    spaceBetween: 16,
    slidesPerView: 11,
    freeMode: true,
    threshold: 5,
    watchSlidesProgress: true
  };
  imagePlaceholder = defaultImagePlaceholder;
  html: HTMLElement;
  scrollY: number;
  activeSlideIndex = 0;
  element: any;
  imagesSwiper;
  thumbsSwiper;
  isHowItWorks: boolean;
  environment = environment;
  private readonly _destruction$: Subject<void> = new Subject<void>();

  @ViewChild('galleryThumbs', { static: false }) galleryThumbs?: ElementRef<
    HTMLElement & { swiper: Swiper } & { initialize: () => void }
  >;
  @ViewChild('galleryImages', { static: false }) galleryImages?: ElementRef<
    HTMLElement & { swiper: Swiper } & { initialize: () => void }
  >;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly dynamicGalleryService: DynamicGalleryService,
    private readonly router: Router,
    private readonly viewportService: ViewportService,
    private readonly resizeService: ResizeService,
    private readonly el: ElementRef,
    private readonly nativeAppService: NativeAppService
  ) {
    this.galleryPics = this.dynamicGalleryService.album;
    this.title = this.dynamicGalleryService.title;
    this.imageIndex = this.dynamicGalleryService.imageIndex;
    this.isShowTags = this.dynamicGalleryService.isShowTags;
    this.isRoomCategory = this.dynamicGalleryService.isRoomCategory;
    this.isHowItWorks = this.dynamicGalleryService.isHowItWorks;
    this.addStylesForDisableScroll(this.document.body);
    this.addStylesForDisableScroll(this.document.documentElement);

    this.activeSlideIndex = this.imageIndex;
    this.element = el.nativeElement;
  }

  ngOnInit() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(take(1))
      .subscribe((event: NavigationEnd) => {
        this.removeComponent();
      });

    this.element.addEventListener('click', (el) => {
      if (el.target.className === 'gallery-wrap') {
        this.removeComponent();
      }
    });
  }

  ngAfterViewInit(): void {
    const swiperInstance = this.galleryImages?.nativeElement?.swiper;
    if (swiperInstance) {
      swiperInstance.slideTo(this.imageIndex, 0);
    }

    this.galleryImages?.nativeElement.addEventListener('swiperslidechange', () => {
      const currentSwiper = this.galleryImages?.nativeElement?.swiper;
      if (currentSwiper) {
        this.activeSlideIndex = currentSwiper.activeIndex;
      }
    });
  }

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  slideToPrev(): void {
    this.galleryImages?.nativeElement.swiper.slidePrev();
  }

  slideToNext(): void {
    this.galleryImages?.nativeElement.swiper.slideNext();
  }

  updateDefaultImageSizes(isPortrait: boolean) {
    if (this.isMobile) {
      if (isPortrait) return '?w=800&h=1000&auto=format&fit=crop&q=60';
      return '?w=800&h=640&auto=format&fit=crop&q=60';
    }

    if (!this.isMobile) {
      if (isPortrait) return '?w=1040&h=1300&auto=format&fit=crop&q=60';
      return '?w=1040&h=832&auto=format&fit=crop&q=60';
    }

    if (isPortrait) return '?w=1300&h=1625&auto=format&fit=crop&q=60';
    return '?w=1300&h=1040&auto=format&fit=crop&q=60';
  }

  removeComponent() {
    this.nativeAppService.notifyIntentToHideOverlay();
    this.dynamicGalleryService.removeComponent();
    this.addStylesForEnableScroll(this.document.body);
    this.addStylesForEnableScroll(this.document.documentElement);
  }

  addStylesForEnableScroll(elem: HTMLElement) {
    elem.style.position = '';
    elem.style.top = '';
    elem.style.overflow = '';
    window.scrollTo(0, parseInt(`${this.scrollY}` || '0'));
  }

  addStylesForDisableScroll(elem: HTMLElement) {
    if (window.scrollY) {
      this.scrollY = window.scrollY;
      elem.style.top = `-${this.scrollY}px`;
    }
    elem.style.position = 'fixed';
    elem.style.overflow = 'hidden';
  }
}
